import { JSX } from "react";
import Chip from "@mui/material/Chip";
import { AccessLevel, Resource, ResourceType, Scope } from "permissions/entities/permission/resources/general";

export enum LoginAccessType {
  'Self',
  'Entity-Wide'
}
export interface ILoginAccessResource {
  isDisabled?: boolean;
  rawText?: string;
  accessType?: string;
}
export interface IUserNetworkingAccessResource {
  isDisabled?: boolean;
  rawText?: string;
  accessLevel?: string;
  scope?: string;
}

export class LoginAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.LoginAccess;
  public accessType?: LoginAccessType
  public rawText?: string;

  public get metaText(): string {
    return `login ${this.accessType}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Login</strong><span>
      {`: ${this.accessType}`}</span></>} />;
  }

  public toString(): string {
    return `Login: ${this.accessType}`;
  }

  public fromObject(obj: ILoginAccessResource): Resource {
    const resource = new LoginAccess();
    resource.rawText = obj.rawText || "";
    resource.accessType = LoginAccessType[obj.accessType as keyof typeof LoginAccessType];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessType: typeof this.accessType === 'string' ? LoginAccessType[this.accessType] : this.accessType,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): LoginAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessType', Object.keys(LoginAccessType).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(LoginAccess, fieldIterations) as LoginAccess[];
  }
}

export class UserNetworkingAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.UserNetworkingAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `networking ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Networking</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Networking: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IUserNetworkingAccessResource): Resource {
    const resource = new UserNetworkingAccess();
    resource.rawText = obj.rawText || "";
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): UserNetworkingAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(UserNetworkingAccess, fieldIterations) as UserNetworkingAccess[];
  }
}
